import React, {useEffect, useState} from "react";
import {Flags} from "../components/Flags";
import {Box, Button, Checkbox, CircularProgress, Container, FormControlLabel, IconButton, InputAdornment, Menu, MenuItem, Paper, TextField, Typography,} from "@mui/material";
import {Language as LanguageIcon, Visibility, VisibilityOff} from "@mui/icons-material";
import {gql, useLazyQuery, useMutation} from "@apollo/client";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

const SIGN_IN = gql`
    mutation SignIn($input: SignInInput!) {
        signIn(input: $input) {
            accessToken
            refreshToken
            role
        }
    }
`;
const WHOAMI_USER = gql`
    query WhoAmIUser {
        whoAmIUser {
            id
            firstName
            lastName
            email
            master
            updatedTimestamp
            deleted
        }
    }
`;

const WHOAMI_RESELLER = gql`
    query WhoAmIResellerUser {
        whoAmIResellerUser {
            id
            firstName
            lastName
            email
            reseller {
                name
            }
            updatedTimestamp
            deleted
        }
    }
`;

const WHOAMI_CUSTOMER = gql`
    query WhoAmICustomerUser {
        whoAmICustomerUser {
            id
            firstName
            lastName
            email
            customer {
                name
                reseller {
                    name
                }
            }
            updatedTimestamp
            deleted
        }
    }
`;


const Login = () => {
    const {t, i18n} = useTranslation();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({username: "", password: ""});
    const [fieldErrors, setFieldErrors] = useState({username: "", password: ""});
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [capsLock, setCapsLock] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [signIn, {loading}] = useMutation(SIGN_IN);
    const [getWhoAmIUser] = useLazyQuery(WHOAMI_USER);
    const [getWhoAmIReseller] = useLazyQuery(WHOAMI_RESELLER);
    const [getWhoAmICustomer] = useLazyQuery(WHOAMI_CUSTOMER);


    const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        localStorage.setItem("i18nextLng", lang);
        handleMenuClose();
    };

    const handleChange = (e) => {
        setFormData({...formData, [e.target.name]: e.target.value});
        setFieldErrors((prev) => ({...prev, [e.target.name]: ""}));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !loading) handleLogin();
    };

    const handleKeyUp = (e) => {
        setCapsLock(e.getModifierState("CapsLock"));
    };

    const handleLogin = async () => {
        if (loading) return;

        setFieldErrors({username: "", password: ""});
        const newErrors = {};

        if (!formData.username.trim() && !formData.password.trim()) {
            newErrors.username = t("userNameNotEmpty");
            newErrors.password = t("passwordNotEmpty");
            setFieldErrors(newErrors);
            return;
        } else if (!formData.username.trim()) {
            newErrors.username = t("userNameNotEmpty");
            setFieldErrors(newErrors);
            return;
        } else if (!formData.password.trim()) {
            newErrors.password = t("passwordNotEmpty");
            setFieldErrors(newErrors);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.username.trim())) {
            setFieldErrors({username: t("emailFormatError")});
            return;
        }

        try {
            const {data} = await signIn({variables: {input: formData}});
            const result = data?.signIn;
            if (result) {
                const storage = rememberMe ? localStorage : sessionStorage;
                storage.setItem("username", formData.username);
                storage.setItem("accessToken", result.accessToken);
                storage.setItem("role", result.role);
                storage.setItem("refreshToken", result.refreshToken);

                try {
                    let whoData = null;
                    if (result.role === "ADMIN") {
                        const {data} = await getWhoAmIUser({fetchPolicy: "no-cache"});
                        whoData = data?.whoAmIUser;
                    } else if (result.role === "RESELLER") {
                        const {data} = await getWhoAmIReseller({fetchPolicy: "no-cache"});
                        whoData = data?.whoAmIResellerUser;
                    } else if (result.role === "CUSTOMER") {
                        const {data} = await getWhoAmICustomer({fetchPolicy: "no-cache"});
                        whoData = data?.whoAmICustomerUser;
                    }

                    if (whoData) {
                        storage.setItem("firstName", whoData.firstName || "");
                        storage.setItem("lastName", whoData.lastName || "");
                        storage.setItem("email", whoData.email || "");
                        storage.setItem("id", whoData.id || "");

                        if (result.role === "RESELLER") {
                            storage.setItem("resellerName", whoData.reseller?.name || "");
                        } else if (result.role === "CUSTOMER") {
                            storage.setItem("customerName", whoData.customer?.name || "");
                            storage.setItem("resellerName", whoData.customer?.reseller?.name || "");
                        }
                    }
                } catch (e) {
                    console.warn("whoAmI sorgusu başarısız:", e);
                }


                setTimeout(() => {
                    navigate("/app/dashboard");
                }, 0);
            }
        } catch (error) {
            const reason = error?.graphQLErrors?.[0]?.extensions?.reason || "UNKNOWN_ERROR";
            const newErrs = {username: "", password: ""};

            if (reason === "ACCOUNT_NOT_ACTIVATED") {
                navigate("/activate", {
                    state: {
                        username: formData.username,
                        defaultPassword: formData.password,
                    },
                });
            } else if (reason === "BAD_CREDENTIALS") {
                newErrs.password = t("userNameOrPassError");
                newErrs.username = t("userNameOrPassError");
            } else if (reason === "DATA_VIOLATION") {
                newErrs.username = t("emptyFieldsError");
                newErrs.password = t("emptyFieldsError");
            } else {
                newErrs.password = t("serverLoginError");
            }
            setFieldErrors(newErrs);
        }
    };

    useEffect(() => {
        ["accessToken", "refreshToken", "role", "username"].forEach((key) => {
            localStorage.removeItem(key);
            sessionStorage.removeItem(key);
        });
    }, []);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#f5f5f5",
                position: "relative",
            }}
        >
            <Container maxWidth="xs">
                <Paper
                    elevation={2}
                    sx={{
                        p: 4,
                        bgcolor: "white",
                    }}
                >
                    {/* Language Selector */}
                    <Box sx={{display: "flex", justifyContent: "flex-end", mb: 2}}>
                        <IconButton
                            onClick={handleMenuOpen}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            <LanguageIcon/>
                        </IconButton>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                        transformOrigin={{vertical: "top", horizontal: "right"}}
                    >
                        <MenuItem
                            onClick={() => changeLanguage("tr")}
                            selected={i18n.language === "tr"}
                            sx={{gap: 1}}
                        >
                            {Flags.tr}
                            <Typography variant="body2">{t("trLan")}</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => changeLanguage("en")}
                            selected={i18n.language === "en"}
                            sx={{gap: 1}}
                        >
                            {Flags.gb}
                            <Typography variant="body2">{t("enLan")}</Typography>
                        </MenuItem>
                    </Menu>

                    {/* Title */}
                    <Box sx={{mb: 4, textAlign: "center"}}>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontWeight: 700,
                                color: "primary.main",
                                mb: 0.5,
                            }}
                        >
                            {t("loginTitle")}
                        </Typography>
                    </Box>

                    {/* Form Fields */}
                    <Box>
                        <TextField
                            label={t("userNameLabel")}
                            name="username"
                            autoFocus
                            value={formData.username}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            fullWidth
                            margin="normal"
                            error={Boolean(fieldErrors.username)}
                            helperText={fieldErrors.username || ""}
                        />

                        <TextField
                            label={t("passwordLabel")}
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={formData.password}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            onKeyUp={handleKeyUp}
                            fullWidth
                            margin="normal"
                            autoComplete="new-password"
                            error={Boolean(fieldErrors.password)}
                            helperText={fieldErrors.password || ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {capsLock && (
                            <Typography
                                color="warning.main"
                                variant="caption"
                                sx={{
                                    display: "block",
                                    mt: 0.5,
                                }}
                            >
                                {t("capsLockOnWarning")}
                            </Typography>
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                />
                            }
                            label={<Typography variant="body2">{t("rememberMe")}</Typography>}
                            sx={{mt: 1, mb: 1}}
                        />

                        <Button
                            variant="contained"
                            onClick={handleLogin}
                            disabled={loading}
                            fullWidth
                            sx={{
                                mt: 2,
                                py: 1.25,
                                textTransform: "none",
                                fontWeight: 600,
                            }}
                        >
                            {loading ? (
                                <CircularProgress sx={{color: "white"}}/>
                            ) : (
                                t("loginButton")
                            )}
                        </Button>
                    </Box>

                    {/* Footer */}
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                            display: "block",
                            textAlign: "center",
                            mt: 3,
                        }}
                    >
                        © 2026 Maksi Filtrasyon
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;
